import {ChangeDetectionStrategy, Component, DestroyRef, Injectable, Injector, input, output} from "@angular/core";
import {Observable, switchMap} from "rxjs";
import {takeUntilDestroyed, toObservable} from "@angular/core/rxjs-interop";
import {CollectionViewer, DataSource} from "@angular/cdk/collections";
import {Mappe, Node, NodeAvhengigheter, Rot} from "../arkiv-trevisning/node";
import {MatTree, MatTreeNode, MatTreeNodeDef, MatTreeNodePadding, MatTreeNodeToggle} from "@angular/material/tree";
import {MatIconButton} from "@angular/material/button";
import {MatIcon} from "@angular/material/icon";
import {MatRadioButton, MatRadioGroup} from "@angular/material/radio";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {ArkivMappenodeModell, ArkivnodeId, ArkivNodeModell, DokumentVersjonModell, GruppeId} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {DokumentFormatIkonKomponent} from "../../../dokument/komponenter/dokument-format-ikon/dokument-format-ikon";
import {MatProgressSpinner} from "@angular/material/progress-spinner";
import {TranslateModule} from "@ngx-translate/core";
import {FormControl, ReactiveFormsModule, Validators} from "@angular/forms";
import {ArkivDatatjeneste} from "felles-ng2/tjenester/datalager/ArkivDatatjeneste";
import {stiTilNode} from "felles-ng2/modell/arkivModellHjelpere";
import {erIkkeTomListe} from "felles-ng2/støttefunksjoner/lister";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {DataOgMeta} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {DokumentInformasjon, DokumentInformasjonTjeneste} from "felles-ng2/tjenester/DokumentInformasjonTjeneste";

const debug = initDebugger('ArkivVelgNodeKomponent');

export type KanNodeVelges = (node: ArkivNodeModell) => boolean;
type SkjemaType = ReturnType<typeof ArkivVelgNodeKomponent.opprettSkjema>;


/**
 * Tre-visning av arkivnoder som lar bruker velge en node
 */
@Component({
    selector: 'arkiv-velg-node',
    templateUrl: 'arkiv-velg-node.html',
    styleUrl: 'arkiv-velg-node.scss',
    host: {'data-testid': 'arkiv-velg-node'},
    imports: [
        MatTree,
        MatIconButton,
        MatTreeNode,
        MatIcon,
        MatTreeNodeToggle,
        MatTreeNodePadding,
        MatTreeNodeDef,
        MatRadioGroup,
        MatRadioButton,
        DokumentFormatIkonKomponent,
        MatProgressSpinner,
        TranslateModule,
        ReactiveFormsModule
    ],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ArkivVelgNodeKomponent {

    /** KanNodeVelges filter som kun tillater mapper for valg */
    static readonly kunMapper: KanNodeVelges = node => node.erMappe;

    /** KanNodeVelges filter som kun tillater dokumenter for valg */
    static readonly kunDokumenter: KanNodeVelges = node => !node.erMappe;

    static opprettSkjema() {
        return new FormControl<ArkivNodeModell | null>(null, {validators: Validators.required});
    }

    vedValgt = output<ArkivNodeModell>();

    gruppeId = input.required<GruppeId>();
    kontroll = input.required<SkjemaType>();
    initieltValgt = input<ArkivNodeModell | null>(null);
    kanVelges = input<KanNodeVelges>(() => true);
    ariaLabel = input<string | null>(null);
    ariaLabelledBy = input<string | null>(null);

    /** Sann dersom node representerer en mappe */
    erMappe = (_: number, node: Node) => node.erMappe();

    dataSource: DataSource<Node>;
    rotnode!: Rot; //settes av lastRotmappe

    constructor(private avhengigheter: ArkivVelgNodeKomponentAvhengigheter, private destroyRef: DestroyRef, private injector: Injector) {
        this.dataSource = {
            connect: (collectionViewer: CollectionViewer): Observable<Node[]> => this.ekspanderteMapper(),
            disconnect: (collectionViewer: CollectionViewer) => this.rotnode.terminer()
        }
    }

    /** Sann dersom node kan velges */
    erValgbar(node: Node) {
        return this.kanVelges()(node.node);
    }

    nivaa(dataNode: Node): number {
        return dataNode.nivaa;
    }

    /** Brukes av tree-komponenten for å avgjøre om noder har blitt endret */
    trackBy(indeks: number, node: Node) {
        return node.node.id
    }

    ekspander(mappe: Mappe, ekspandert: boolean) {
        if (ekspandert) mappe.åpne();
        else mappe.lukk();
    }

    //hjelpemetoder

    private ekspanderteMapper(): Observable<Node[]> {
        return toObservable(this.gruppeId, {injector: this.injector})
          .pipe(
            switchMap(gruppeId => this.avhengigheter.rotmappe(gruppeId)),
            switchMap(rotnode => {
                this.rotnode = new Rot(rotnode, this.avhengigheter);
                //sørge for å vise forespurt node, dersom angitt
                if (erIkkeNil(this.initieltValgt()))
                    this.markerSomValgt(this.initieltValgt()!);
                return this.rotnode.somUtflatetObserverbar();
            }),
            takeUntilDestroyed(this.destroyRef),
          )
    }

    private async markerSomValgt(node: ArkivNodeModell) {
        debug(`markerSomValgt node: ${node.id} (erMappe:${node.erMappe})`)
        let sti = stiTilNode(node);
        sti.pop() //grupperot har vi allerede
        //legg til alle foreldrene i mappe-treet vårt
        let grupperotBarn = sti.pop()!;
        let barn = this.rotnode!.barn().find(barn => barn.node.id == grupperotBarn.id);
        while (erIkkeTomListe(sti) && erIkkeNil(barn) && barn.erMappe()) {
            barn.åpne(); //åpner noden
            barn = barn.leggTilBarn(sti.pop()!)[0] as Mappe;
        }
        if (erNil(barn)) return;
        //sett som valgt
        this.kontroll().setValue(barn.node);
    }

}


@Injectable()
export class ArkivVelgNodeKomponentAvhengigheter implements NodeAvhengigheter {

    constructor(private arkivDatatjeneste: ArkivDatatjeneste, private dokumentInformasjonTjeneste: DokumentInformasjonTjeneste) {
    }

    rotmappe(gruppeId: GruppeId): Observable<ArkivMappenodeModell> {
        return this.arkivDatatjeneste.rotmappeForGruppe(gruppeId);
    }

    // NodeAvhengigheter implementasjon

    dokumentinformasjon(dokumentversjon: DokumentVersjonModell): DokumentInformasjon {
        return this.dokumentInformasjonTjeneste.dokumentinformasjon(dokumentversjon);
    }

    barn(forelderId: ArkivnodeId): Observable<DataOgMeta<ArkivNodeModell[]>> {
        return this.arkivDatatjeneste.barnMedMeta(forelderId);
    }
}
